/**
 * Simple Health Check for WebSocket
 * 
 * Responsibilities:
 * 1. Send: emit ping payload to backend (via outbound)
 * 2. Receive: handle pong payload and set lastPongTimestamp (via inbound)
 * 3. checkHealthStatus: determine if connected or disconnected based on lastPongTimestamp
 * 4. getHealthStatus: return status and lastPongTimestamp
 */

import type { Connection, CommunicationManager } from './outbound';
import type { ConnectionState } from './inbound';
import type { VishMakerPayload } from './outbound';

export type HealthStatus = 'connected' | 'disconnected';

export interface HealthCheckStatus {
    lastPongTimestamp: number | null;
    status: HealthStatus;
}

export interface HealthCheckCallbacks {
    onStateChange: (state: ConnectionState) => void;
}

export class HealthCheck {
    private conn: Connection | null = null;
    private comm: CommunicationManager | null = null;
    private lastPongTimestamp: number | null = null;
    private callbacks: HealthCheckCallbacks;
    private healthCheckTimer: number | null = null;
    private currentState: ConnectionState = 'disconnected';
    
    // Configuration
    private readonly healthTimeout = 2 * 60 * 1000; // 2 minutes
    private readonly healthCheckInterval = 15000; // 15 seconds

    constructor(callbacks: HealthCheckCallbacks) {
        this.callbacks = callbacks;
    }

    /**
     * Initialize health check with connection objects
     */
    initialize(conn: Connection, comm: CommunicationManager): void {
        this.conn = conn;
        this.comm = comm;
        this.lastPongTimestamp = null;
    }

    /**
     * Start health check system
     * Sends ping every healthCheckInterval (15 seconds) and checks status
     */
    start(): void {
        this.stop(); // Stop any existing timer
        
        console.log('🔌 WebSocket Initiated');
        
        // Send ping and check health status periodically
        this.healthCheckTimer = window.setInterval(() => {
            this.send(); // Send ping
            this.checkHealthStatus(); // Check if we have recent pong
        }, this.healthCheckInterval);
    }

    /**
     * Stop health check system
     */
    stop(): void {
        if (this.healthCheckTimer !== null) {
            clearInterval(this.healthCheckTimer);
            this.healthCheckTimer = null;
        }
    }

    /**
     * Reset health tracking
     */
    reset(): void {
        this.lastPongTimestamp = null;
    }

    /**
     * Get current connection state
     */
    getConnectionState(): ConnectionState {
        return this.currentState;
    }

    /**
     * Set connection state
     */
    setConnectionState(state: ConnectionState): void {
        this.currentState = state;
    }

    /**
     * Send: Emit ping payload to backend
     * Uses outbound to send the ping
     */
    send(): void {
        if (!this.conn || !this.comm) {
            return;
        }

        // Set up health channel context if not exists
        if (!this.conn.getChannelContext('health')) {
            const messageId = crypto.randomUUID();
            const timestamp = new Date().toISOString();
            this.conn.setChannelContext(
                'health',
                {
                    threadId: `health_${Date.now()}`,
                    originMessageId: messageId,
                    originActor: 'user',
                    origints: timestamp
                },
                messageId,
                'user',
                timestamp
            );
        }

        // Send ping
        const pingTimestamp = new Date().toISOString();
        const pingPayload = {
            message: 'ping',
            ts: pingTimestamp
        };

        const ok = this.comm.emit('health', 'user', 'connected', 'status', pingPayload, 'request');
        if (!ok) {
            console.warn(`🔌 [HealthCheck] Failed to send ping ${pingTimestamp}`);
        }
    }

    /**
     * Receive: Handle pong payload from backend
     * Uses inbound to receive the pong
     */
    receive(payload: VishMakerPayload): void {
        const payloadData = payload.body?.payload || payload.body || payload;
        const message = payloadData?.message || payloadData?.body?.message;

        // Check if this is a pong response
        if (message === 'pong') {
            const ts = payloadData?.ts || payload.ts || new Date().toISOString();
            const parsed = new Date(ts).getTime();
            if (Number.isNaN(parsed)) {
                console.warn(`🔌 [HealthCheck] Invalid pong timestamp ${ts}`);
                return;
            }
            this.lastPongTimestamp = parsed;

            // Check status and update if needed
            this.checkHealthStatus();
        }
    }

    /**
     * Get health status - simplified to just return lastPongTimestamp and status
     */
    getHealthStatus(): HealthCheckStatus {
        const status = this.checkHealthStatus();
        return {
            lastPongTimestamp: this.lastPongTimestamp,
            status: status
        };
    }

    /**
     * Check health status and trigger state changes
     * Returns the current health status
     */
    checkHealthStatus(): HealthStatus {
        const now = Date.now();
        const timeSinceLastPong = this.lastPongTimestamp ? now - this.lastPongTimestamp : null;

        // If we have a pong within the timeout, we're connected
        if (timeSinceLastPong !== null && timeSinceLastPong < this.healthTimeout) {
            if (this.currentState !== 'connected') {
                this.currentState = 'connected';
                this.callbacks.onStateChange('connected');
            }
            return 'connected';
        } else {
            // No pong or pong too old - disconnected
            if (this.currentState !== 'disconnected') {
                this.currentState = 'disconnected';
                this.callbacks.onStateChange('disconnected');
            }
            return 'disconnected';
        }
    }
}
